import {MenuItem, useMediaQuery} from '@material-ui/core';
import {CorrespondenceLetterSummary} from 'Agent/Policies/api/apiTypes';
import CorrespondenceDetails from 'Agent/Policies/components/CorrespondenceDetails';
import {
  changeCorrespondenceSummaryFilter,
  clearCorrespondence,
  getCorrespondenceDetails,
  getCorrespondenceSummaries,
  clearSort,
  changeSort,
} from 'Agent/Policies/ducks/policyCorrespondence';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import qs from 'query-string';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import AsyncButton from 'Shared/components/AsyncButton';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {Column} from 'Shared/components/PagedView/Column';
import {withDateFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {prop} from 'Shared/utilities/prop';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectCurrentAgentNumbers} from 'Users/selectors/userProfileSelectors';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import * as O from 'fp-ts/lib/Option';

const getFileDownload = (links: CorrespondenceLetterSummary['links']) => links!.find(link => link.rel === 'pdf');
const getDetails = (links: CorrespondenceLetterSummary['links']) => links!.find(link => link.rel === 'details');

type CorrespondenceSummariesProps = {
  selectedAgency: AgencyFilters;
};

const CorrespondenceSummaries = ({selectedAgency}: CorrespondenceSummariesProps) => {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  useBreadcrumbs('Correspondence', match.url);

  const {correspondenceSummaries, correspondenceSummariesFilter, correspondenceDetails, sort} = useSelector(
    (state: ReducerState) => state.policyCorrespondence
  );
  const agentNumbers = useSelector(selectCurrentAgentNumbers);
  const {search} = useLocation();
  const agentNumber = qs.parse(search).agentNumber as string;
  const policyNumber = qs.parse(search).policyNumber as string;

  useEffect(() => {
    if (agentNumber || policyNumber) {
      dispatch(changeCorrespondenceSummaryFilter({...correspondenceSummariesFilter, agentNumber, policyNumber}));
    }

    return () => {
      dispatch(clearCorrespondence());
      dispatch(clearSort());
    };
  }, [agentNumber]);

  useEffect(() => {
    dispatch(
      getCorrespondenceSummaries(
        agentNumbers,
        selectedAgency.isNewYork,
        selectedAgency.encryptedIdentifier,
        undefined,
        correspondenceSummariesFilter,
        O.toUndefined(sort)
      )
    );
  }, [agentNumbers, correspondenceSummariesFilter, sort]);

  const changeFilter = withValueChangedHook(correspondenceSummariesFilter, changeCorrespondenceSummaryFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: correspondenceSummariesFilter,
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredCorrespondenceSummaries',
    relativeFileLocation: `/policies/correspondences${addFilterParameters({
      ...correspondenceSummariesFilter,
      agentNumbers: agentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
      exportToCsv: true,
    })}`,
    children: 'Export To CSV',
  };

  return (
    <>
      <CorrespondenceDetails />
      <PagedView
        pagedViewKey="correspondenceSummaries"
        sort={O.toUndefined(sort)}
        onChangeSort={sort => {
          dispatch(changeSort(sort));
        }}
        data={correspondenceSummaries}
        loadMore={page =>
          dispatch(
            getCorrespondenceSummaries(
              agentNumbers,
              selectedAgency.isNewYork,
              selectedAgency.encryptedIdentifier,
              page,
              correspondenceSummariesFilter,
              O.toUndefined(sort)
            )
          )
        }
        actions={
          isMobile ? (
            <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
          ) : (
            <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
          )
        }
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="Letter Number"
              sortColumnKey="number"
              filterProperties={withStringFilterProperties(filterProperties, 'letterNumber')}
              default
            >
              {letter => letter.number}
            </Column>
            <Column
              {...columnProps}
              title="Agent Number"
              sortColumnKey="agentNumber"
              filterProperties={withStringFilterProperties(filterProperties, 'agentNumber')}
              default
            >
              {letter => letter.agentNumber}
            </Column>
            <Column
              {...columnProps}
              title="Description"
              sortColumnKey="description"
              filterProperties={withStringFilterProperties(filterProperties, 'letterDescription')}
              default
            >
              {letter => letter.description}
            </Column>
            <Column
              {...columnProps}
              title="Insured Name"
              sortColumnKey="insuredName"
              filterProperties={withStringFilterProperties(filterProperties, 'insuredName')}
              default
            >
              {letter => letter.insuredName}
            </Column>
            <Column
              {...columnProps}
              title="Policy Number"
              sortColumnKey="policyNumber"
              filterProperties={withStringFilterProperties(filterProperties, 'policyNumber')}
              default
            >
              {letter => letter.policyNumber}
            </Column>
            <Column
              {...columnProps}
              title="Account Number"
              sortColumnKey="accountNumber"
              filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
              default
            >
              {letter => letter.accountNumber}
            </Column>
            <Column
              {...columnProps}
              title="Date"
              sortColumnKey="date"
              filterProperties={withDateFilterProperties(filterProperties, 'letterDate')}
              default
            >
              {letter => toFormattedDateAsUTC(letter.date)}
            </Column>
            <Column {...columnProps} title="" isActions default>
              {letter => {
                const fileDownload = getFileDownload(letter.links);
                const detailsLink = getDetails(letter.links);

                return (
                  <>
                    {detailsLink && (
                      <AsyncButton
                        className="correspondence-summary__view-details-button"
                        data={correspondenceDetails}
                        color="primary"
                        onClick={() =>
                          dispatch(
                            getCorrespondenceDetails(
                              detailsLink.href! +
                                '&' +
                                addFilterParameters({encryptedIdentifier: selectedAgency.encryptedIdentifier}).slice(1)
                            )
                          )
                        }
                      >
                        View Details
                      </AsyncButton>
                    )}
                    {fileDownload && (
                      <ActionsMenu>
                        <MenuItem
                          onClick={() => {
                            downloadFile(
                              fileDownload.href! +
                                addFilterParameters({encryptedIdentifier: selectedAgency.encryptedIdentifier}),
                              `${letter.insuredName}-${letter.description}-${toFormattedDateAsUTC(letter.date)}.pdf`
                            );
                          }}
                        >
                          Download
                        </MenuItem>
                      </ActionsMenu>
                    )}
                  </>
                );
              }}
            </Column>
          </>
        )}
      </PagedView>
    </>
  );
};

export default CorrespondenceSummaries;
